/* oxlint-disable local-rules/enforce-generate-path -- API endpoint URLs, not navigation paths */
import { api } from '@/services/cdp-api';
import type {
  Source,
  SourceMetrics,
  Schema,
  SourceEvent,
  SourceApiKey,
  SourceOption,
  SourceSync,
  SourceSyncRun,
  ConnectorSourceJourneysObjectMapping,
  MessageMetricsSourceSettings,
} from '.';
import type { OauthProvidersConfig, OauthCredentialConfig as OauthConnection } from '@/features/pipelines/oauth';
import type { MetricsDuration } from '@/utils/time';
import type { DestinationDefinition as SourceDefinition } from '@customerio/cdp-action-destinations';
import type { CreateOrUpdateSourceSyncCmd, ConnectorSourceRecord } from '.';
import type { DestinationMode } from '../destinations';

type ContinuationRequestExtended = {
  after?: string;
  before?: string;
};

type Meta = {
  continuation?: {
    first?: string;
    last?: string;
  };
  pagination?: {
    page: number;
    total: number;
  };
};

export type SourceId = number | 'journeys';

export const getSource = async (workspaceId: number, sourceId: SourceId) => {
  const { data } = await api.get<Source>(`/workspaces/${workspaceId}/sources/${sourceId}`);
  return data;
};

export const createSource = async (
  workspaceId: number,
  { optionId, name, settings }: { optionId: string; name: string; settings?: Record<string, unknown> },
) => {
  const { data } = await api.post<Source>(`/workspaces/${workspaceId}/sources`, {
    source: {
      name,
      option_id: optionId,
      settings,
    },
  });
  return data;
};

export const updateSource = async (
  workspaceId: number,
  sourceId: SourceId,
  {
    name,
    enabled,
    settings,
    geolocation_enabled,
  }: {
    name: string;
    enabled: boolean;
    settings?: Record<string, unknown>;
    geolocation_enabled?: boolean;
  },
) => {
  const { data } = await api.put<Source>(`/workspaces/${workspaceId}/sources/${sourceId}`, {
    source: {
      name,
      enabled,
      ...(settings != null ? { settings } : {}),
      ...(geolocation_enabled != null ? { geolocation_enabled } : {}),
    },
  });
  return data;
};

export const deleteSource = async (workspaceId: number, sourceId: number) => {
  const { data } = await api.delete(`/workspaces/${workspaceId}/sources/${sourceId}`);
  return data;
};

type GetSourceStatusResponse = {
  status: 'no_recent_data' | 'enabled' | 'disabled';
  last_seen: number;
};

export const getSourceStatus = async (workspaceId: number, sourceId: SourceId) => {
  const { data } = await api.get<GetSourceStatusResponse>(`/workspaces/${workspaceId}/sources/${sourceId}/status`);
  const { status, last_seen: lastSeen } = data;
  return { status, lastSeen };
};

export const getSourcesLastEvents = async (workspaceId: number) => {
  const { data } = await api.get<{
    statuses: { [key: string]: number };
  }>(`/workspaces/${workspaceId}/sources/statuses`);
  return data.statuses;
};

export const getSourceMetrics = async (
  workspaceId: number,
  sourceId: SourceId,
  event?: 'all' | 'identify' | 'track' | 'screen' | 'group' | 'alias' | 'page',
  duration?: MetricsDuration,
) => {
  const { data } = await api.get<{ metrics: SourceMetrics }>(`/workspaces/${workspaceId}/sources/${sourceId}/metrics`, {
    params: {
      event,
      duration,
    },
  });
  return data.metrics;
};

export const getSourceSchema = async (
  workspaceId: number,
  sourceId: SourceId,
  event?: 'identify' | 'track' | 'screen' | 'group' | 'alias' | 'page',
) => {
  const { data } = await api.get<{ schema: Schema }>(`/workspaces/${workspaceId}/sources/${sourceId}/schema`, {
    params: {
      type: event,
    },
  });
  return data.schema;
};

export const getSourceEvents = async (
  workspaceId: number,
  sourceId: SourceId,
  params: {
    eventTypes?: string[];
    recordTypes?: string[];
    syncNames?: string[];
    search?: string;
    messageId?: string;
    limit?: number;
    continuation?: ContinuationRequestExtended;
  } = {},
) => {
  const apiParams = {
    eventTypes: params.eventTypes,
    recordTypes: params.recordTypes,
    syncNames: params.syncNames,
    search: params.search,
    messageId: params.messageId,
    limit: params.limit ?? 100,
    ...params.continuation,
  };
  const { data } = await api.get(`/workspaces/${workspaceId}/sources/${sourceId}/events`, {
    params: apiParams,
    paramsSerializer: {
      indexes: null,
    },
  });
  return data;
};

export const getSourceEventById = async (
  workspaceId: number,
  sourceId: SourceId,
  messageId: string,
  serverId: string,
  position: string,
) => {
  const { data } = await api.get<SourceEvent>(`/workspaces/${workspaceId}/sources/${sourceId}/events/${messageId}`, {
    params: { serverId, position },
  });
  return data;
};

export const listSources = async (workspaceId: number) => {
  const { data } = await api.get<{ sources: Source[] }>(`/workspaces/${workspaceId}/sources`);
  return data.sources ?? [];
};

export const setSourceDestinationConnection = async (
  workspaceId: number,
  sourceId: SourceId,
  destinationId: number,
  connect: boolean,
  settings?: ConnectorSourceJourneysObjectMapping,
) => {
  const { data } = await api.post(`/workspaces/${workspaceId}/source_destination`, {
    source_id: sourceId,
    destination_id: destinationId,
    connect,
    settings,
  });

  return data;
};

export const updateSourceDestinationConnection = async (
  workspaceId: number,
  sourceId: SourceId,
  destinationId: number,
  settings?: ConnectorSourceJourneysObjectMapping,
) => {
  const { data } = await api.put(`/workspaces/${workspaceId}/source_destination`, {
    source_id: sourceId,
    destination_id: destinationId,
    settings,
  });

  return data;
};

export const listSourceApiKeys = async (workspaceId: number, sourceId: SourceId) => {
  const { data } = await api.get<{ api_keys: SourceApiKey[] }>(
    `/workspaces/${workspaceId}/sources/${sourceId}/api_keys`,
  );
  return data.api_keys;
};

export const createSourceApiKey = async (workspaceId: number, sourceId: SourceId) => {
  const { data } = await api.post<SourceApiKey[]>(`/workspaces/${workspaceId}/sources/${sourceId}/api_keys`);
  return data;
};

export const deleteSourceApiKey = async (workspaceId: number, sourceId: SourceId, apiKeyId: number) => {
  const { data } = await api.delete(`/workspaces/${workspaceId}/sources/${sourceId}/api_keys/${apiKeyId}`);
  return data;
};

export const listSourceOptions = async (workspaceId: number) => {
  const { data } = await api.get<{ sources: SourceOption[] }>(`/workspaces/${workspaceId}/available_sources`);
  return data.sources;
};

export const getSourceOAuthProviderConfig = async (
  workspaceId: number,
  optionID: string,
  settings: Record<string, unknown> | null,
) => {
  const stringifiedSettings = JSON.stringify(settings ?? '{}');
  const { data } = await api.get<OauthProvidersConfig>(
    `/workspaces/${workspaceId}/sources/connect?option_id=${optionID}&settings=${encodeURIComponent(
      stringifiedSettings,
    )}`,
  );
  return data;
};

export const getSourceOAuthConnection = async (workspaceId: number, sourceId: SourceId) => {
  const { data } = await api.get<OauthConnection>(`/workspaces/${workspaceId}/sources/${sourceId}/connect`);
  return data;
};

export const createSourceOAuthConnection = async (
  workspaceId: number,
  sourceId: SourceId,
  // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
  payload: Record<string, any>,
) => {
  await api.post(`/workspaces/${workspaceId}/sources/${sourceId}/connect`, payload);
};

export const forceSourceOAuthTokenRefresh = async (
  workspaceId: number,
  sourceId: SourceId,
  // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
  payload: Record<string, any>,
) => {
  await api.post(`/workspaces/${workspaceId}/sources/${sourceId}/force_token_refresh`, payload);
};

export const deleteSourceOAuthConnection = async (workspaceId: number, sourceId: SourceId) => {
  await api.delete(`/workspaces/${workspaceId}/sources/${sourceId}/connect`);
};

export const getSourceOptionManifest = async (workspaceId: number, sourceOptionId: string) => {
  const { data } = await api.get<SourceDefinition>(
    `/workspaces/${workspaceId}/action_sources/${sourceOptionId}/manifest`,
  );
  return data;
};

export const createSourceSync = async (workspaceId: number, sourceId: number, sync: CreateOrUpdateSourceSyncCmd) => {
  const { data } = await api.post<SourceSync>(`/workspaces/${workspaceId}/sources/${sourceId}/syncs`, {
    source_sync: {
      ...sync,
      mapping: {
        ...sync.mapping,
        cdp_type: sync.mapping.cdp_type,
      },
    },
  });
  return data;
};

export const toggleSourceSync = async (workspaceId: number, sourceId: number, syncId: number, enabled: boolean) => {
  const { data } = await api.put<SourceSync>(`/workspaces/${workspaceId}/sources/${sourceId}/syncs/${syncId}/toggle`, {
    enabled,
  });
  return data;
};

export const updateSourceSync = async (
  workspaceId: number,
  sourceId: number,
  syncId: number,
  sync: CreateOrUpdateSourceSyncCmd,
  confirmRestart?: boolean,
) => {
  // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
  const body: Record<string, any> = {
    source_sync: {
      ...sync,
      mapping: {
        ...sync.mapping,
        cdp_type: sync.mapping.cdp_type,
      },
    },
  };

  if (confirmRestart != null) {
    body.confirm_restart = confirmRestart;
  }

  const { data } = await api.put<SourceSync>(`/workspaces/${workspaceId}/sources/${sourceId}/syncs/${syncId}`, body);
  return data;
};

export const deleteSourceSync = async (workspaceId: number, sourceId: number, syncId: number) => {
  const { data } = await api.delete(`/workspaces/${workspaceId}/sources/${sourceId}/syncs/${syncId}`);
  return data;
};

export const listSourceSyncs = async (workspaceId: number) => {
  const { data } = await api.get<{ syncs: SourceSync[] }>(`/workspaces/${workspaceId}/sources/syncs`);
  return data.syncs;
};

export const listSourceSyncsForSource = async (workspaceId: number, sourceId: SourceId) => {
  const { data } = await api.get<{ syncs: SourceSync[] }>(`/workspaces/${workspaceId}/sources/${sourceId}/syncs`);
  return data.syncs;
};

export const getSourceSync = async (workspaceId: number, sourceId: SourceId, syncId: number) => {
  const { data } = await api.get<SourceSync>(`/workspaces/${workspaceId}/sources/${sourceId}/syncs/${syncId}`);
  return data;
};

export const listSourceSyncRuns = async (
  workspaceId: number,
  sourceId: number,
  params: {
    limit?: number;
    page?: number;
    showOnlyErrors?: boolean;
    syncImportId?: number;
    excludeEmptyRows?: boolean;
  } = {},
) => {
  const apiParams = {
    limit: params.limit ?? 10,
    page: params.page,
    show_only_errors: params.showOnlyErrors ?? false,
    reverse_etl_id: params.syncImportId ?? -1,
    source_sync_id: params.syncImportId ?? -1,
    exclude_zero_total_rows: params.excludeEmptyRows ?? false,
  };
  const url = `/workspaces/${workspaceId}/sources/${sourceId}/sync_runs`;
  const { data } = await api.get<{ runs: SourceSyncRun[]; meta: Meta }>(url, {
    params: apiParams,
  });
  return data;
};

export const getSourceSyncRunErrorFileUrl = async (workspaceId: number, sourceId: number, runId: number) => {
  const url = `/workspaces/${workspaceId}/sources/${sourceId}/sync_runs/${runId}/errors/export`;
  const { data } = await api.get<{ url: string }>(url);
  return data.url;
};

export const listConnectorSourceRecords = async (
  workspaceId: number,
  sourceId: number,
  record?: string,
  includeRelationships?: boolean,
) => {
  const params = {
    object_type: record,
    include_relationships: includeRelationships,
  };

  const { data } = await api.get<{ record_types: ConnectorSourceRecord[] }>(
    `/workspaces/${workspaceId}/sources/${sourceId}/record_types`,
    {
      params,
    },
  );
  return data.record_types;
};

export const runSourceSync = async (
  workspaceId: number,
  sourceId: number,
  syncId: number,
  params: {
    restart?: boolean;
  } = {},
) => {
  const { data } = await api.post(`/workspaces/${workspaceId}/sources/${sourceId}/syncs/${syncId}/runs`, params);
  return data;
};

export const getSourceDestinations = async (workspaceId: number, sourceId: number | string) => {
  const { data } = await api.get<{
    connections: {
      destination: {
        id: number;
        name: string;
        option_id: string;
        mode: DestinationMode;
        enabled: boolean;
      };
      settings: ConnectorSourceJourneysObjectMapping;
    }[];
  }>(`/workspaces/${workspaceId}/source_destination?source_id=${sourceId}`);
  return (data.connections ?? []).map((sd) => ({
    ...sd.destination,
    connection_settings: sd.settings,
  }));
};

// Validate SOQL where clauses used in filtering records when reading from Salesforce using a Source Sync
export const validateWhereClause = async (
  workspaceId: number,
  sourceId: number,
  {
    sobject_name,
    where_clause,
    identifiers,
  }: {
    sobject_name: string;
    where_clause: string;
    identifiers?: {
      user_id?: string;
      group_id?: string;
      cdp_type?: string;
      object_name?: string;
    };
  },
) => {
  const { data } = await api.post(`/workspaces/${workspaceId}/sources/${sourceId}/validate_salesforce_filter`, {
    sobject_name,
    where_clause,
    identifiers,
  });
  return data;
};

export const getMessageMetricsSourceSettings = async (workspaceId: number, sourceId: number) => {
  const { data } = await api.get<MessageMetricsSourceSettings>(
    `/workspaces/${workspaceId}/sources/${sourceId}/settings`,
  );
  return data;
};

export const updateMessageMetricsSourceSettings = async (
  workspaceId: number,
  sourceId: number,
  settings: MessageMetricsSourceSettings,
) => {
  const { data } = await api.put(`/workspaces/${workspaceId}/sources/${sourceId}/settings`, settings);
  return data;
};

export interface SourceSyncPreviewIdentifiers {
  object_name?: string;
  event?: string;
  user_id?: string;
  group_id?: string;
}

export interface SourceSyncPreviewRequest {
  object_type: string;
  cdp_type: string;
  identifiers?: SourceSyncPreviewIdentifiers;
  fields: string[];
  renames?: Record<string, string>;
  where_clause?: string;
  limit?: number;
}

export interface SourceSyncPreviewResponse {
  object_type: string;
  // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
  sample_data: Array<Record<string, any>>;
  // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
  raw_data: Array<Record<string, any>>;
  total_count: number;
  excluded_count?: number;
}

export const previewSourceSync = async (
  workspaceId: number,
  sourceId: SourceId,
  request: SourceSyncPreviewRequest,
): Promise<SourceSyncPreviewResponse> => {
  const { data } = await api.post<SourceSyncPreviewResponse>(
    `/workspaces/${workspaceId}/sources/${sourceId}/syncs/preview`,
    request,
  );
  return data;
};

// --- Workspace/object-type API calls (used by source wizards) ---

export interface Workspace {
  account_id: number;
  id: number;
  identifiers: Record<string, number>;
  name: string;
}

interface ObjectType {
  id: number;
  name: string;
  icon?: string;
  slug: string;
  color?: string;
}

export const getWorkspace = async (workspaceId: number) => {
  const { data } = await api.get<Workspace>(`/workspaces/${workspaceId}`);
  return data;
};

export const listObjectTypes = async (workspaceId: number) => {
  const { data } = await api.get<{ object_types: ObjectType[] }>(`/workspaces/${workspaceId}/object_types`);
  return data.object_types;
};

export const createObjectTypeApi = async (
  workspaceId: number,
  objectType: {
    name: string;
    slug: string;
    singular_name: string;
    singular_slug: string;
  },
) => {
  const { data } = await api.post<ObjectType>(`/workspaces/${workspaceId}/object_types`, {
    object_type: objectType,
  });
  return data;
};
